import {Memo, MemoItem} from "../Memo";
import {Point, PropertyObject} from "../lib/memoTypes";
import {keyname} from "../util/DomUtils";
import {Expression, PureExpressionResult} from "../util/expr/Expr";
import $ from "jquery";
import {ContextToolbar} from "../DomMenu";
import {Action, ActionManager, Command, CommandProperties, DynamicToolbarSource} from "../ActionManager";
import 'jquery-ui/ui/widgets/resizable';
import {observable} from "../lib/signal";

export class SelectionManager<DocumentType extends Memo, ItemType extends MemoItem & { parent: DocumentType }> extends ActionManager {
    private _selectedItems: ItemType[] = [];
    dragStartElement: ItemType;
    dragStartOffset: Point;
    @observable
    activeMemo: DocumentType;
    lastCopiedToClipboard : PropertyObject[] | null = null;
    constructor(toolbarItems: CommandProperties[]) {
        super(toolbarItems);
        this.actionTargets['memo'] = (method: string) => [this.activeMemo, method];
    }
    get selectedItems(): ItemType[] {
        return this._selectedItems;
    }
    set selectedItems(val: ItemType[]) {
        this.deselectAll();
        this.selectMultiple(val);
    }
    async getDynamicToolbarSources(): Promise<Set<DynamicToolbarSource>> {
        var relevantMemos = await super.getDynamicToolbarSources();
        if (this.activeMemo && 'getToolbarItemDefs' in this.activeMemo) relevantMemos.add(this.activeMemo as any);
        return relevantMemos;
    }
    async handleKeyEvent(e: KeyboardEvent) {
        if (await super.handleKeyEvent(e, this._selectedItems)) return true;
        let key = keyname(e);
        if (key == 'Escape' && this._selectedItems.length) {
            this.deselectAll();
            e.preventDefault(); e.stopPropagation();
            return true;
        }
        return false;
    }
    isMemoFocused(ignoreFocus=false) {
        return this.activeMemo && (ignoreFocus || document.activeElement === document.body || document.activeElement === this.activeMemo.scrollContainer);
    }
    isToolbarEnabled(toolbarName: string, targetItems?: { props: PropertyObject }[], ignoreFocus = false): boolean {
        switch(toolbarName) {
            case 'main': case 'global': return true;
            case 'memo': return this.isMemoFocused(ignoreFocus);
            case (this.activeMemo?.options?.newrectTb||'newrect'): return this.isMemoFocused(ignoreFocus) && !!this.activeMemo?.rubberbandRect;
            case 'hidden': case 'itemctx': case 'single': case 'itemprops': return !((targetItems || this._selectedItems).length < 1 || !this.isMemoFocused(ignoreFocus))
            case 'multi': return (targetItems || this._selectedItems).length >= 2 && this.isMemoFocused(ignoreFocus);
        }
        return false;
    }
    matchesSelection(toolbarItem: Command, targetItems?: { props: PropertyObject }[]) {
        return this.matchesTarget(toolbarItem, targetItems || this._selectedItems);
    }
    execute(action: Action, event, targetItems: any[], extraContext?: PureExpressionResult): Promise<any> {
        if (!targetItems) targetItems = this._selectedItems;
        return super.execute(action, event, targetItems, extraContext);
    }
    async forEachSelected(fn : (i:MemoItem)=>Promise<void> ) {
        const items = this._selectedItems.slice();
        for (const item of items) await fn(item);
    }
    deselectAll(clearRubberband = true) {
        if (this.activeMemo && clearRubberband) this.activeMemo._clearRubberband();
        for (const k in this._selectedItems)
            if (this._selectedItems) {
                this._selectedItems[k].selected = false;
            }
        this._selectedItems = [];
        this.showAppropriateToolbars();
    }
    setSelection(item:ItemType) {
        this.selectedItems = [item];
    }
    selectMultiple(items:ItemType[], skipToolbar = false) {
        if (this.activeMemo) this.activeMemo._clearRubberband();
        var item : ItemType | null = null;
        items.sort((a, b) => a.getInt('y', 0) - b.getInt('y', 0));
        for (const x of items) {
            if (x.isDeleted) continue;
            item = x;
            if (item.props.nosel) continue;
            if (!item.parent.options.allowSelect) continue;
            if (this._selectedItems.indexOf(item) === -1)
                this._selectedItems.push(item);
            if (item.selected) continue;
            item.selected = true;
        }
        if (item)
            this.activeMemo = item.parent;
        if (!skipToolbar) this.showAppropriateToolbars();
    }
    select(item:ItemType) {
        this.selectMultiple([ item ]);
    }
    deselect(item:ItemType) {
        var i = this._selectedItems.indexOf(item);
        if (i !== -1)
            this._selectedItems.splice(i, 1);
        item.selected = false;
        this.showAppropriateToolbars();
    }
    clearToolbars() {
        this.toolbars.forEach(tb => tb.hide());
        this.toolbars = [];
    }
    async showAppropriateToolbars() {
        if (!this.activeMemo?.rubberbandRect && !this._selectedItems.length) {
            this.clearToolbars();
            return;
        }
        var tbItems = await this.getToolbarItems();
        this.clearToolbars();
        var offsets = this._selectedItems.map(x => $(x.el).offset());
        var x = offsets.reduce((val, offset) => Math.min(offset.left, val), 99999),
            y = offsets.reduce((val, offset) => Math.min(offset.top, val), 99999);
        if (this.activeMemo?.rubberbandRect && !this.activeMemo?.readonly) {
            let rubberbandOffset = this.activeMemo._getScreenCoords(this.activeMemo.rubberbandRect);
            this.toolbars = [
                new ContextToolbar(true, tbItems[this.activeMemo.options.newrectTb].filter(x => this.matchesSelection(x)), this.toolbarClick.bind(this), rubberbandOffset.x, rubberbandOffset.y)
            ];
        } else if (this._selectedItems.length == 1) {
            this.toolbars = [
                new ContextToolbar(true, tbItems.single.filter(x => this.matchesSelection(x)), this.toolbarClick.bind(this), x, y)
            ];
        } else if (this._selectedItems.length > 1) {
            this.toolbars = [
                new ContextToolbar(true, tbItems.single.filter(x => this.matchesSelection(x)), this.toolbarClick.bind(this), x, y),
                new ContextToolbar(false, tbItems.multi.filter(x => this.matchesSelection(x)), this.toolbarClick.bind(this), x, y),
            ];
        }
    }
}